/**
 * @todo This file needs more documentation
 * @module "form.object.util"
 */

define('formkit/form.object.util',['require','exports','module','underscore','backbone','bluebird','uuid','./model/answer.model'],function(require, exports, module) {
  //prettier-ignore
  var _           = require('underscore'),
      Backbone    = require('backbone'),
      Promise     = require('bluebird'),
      UUID        = require('uuid'),
      AnswerModel = require('./model/answer.model');

  var FormObjectUtil = {
    /**
     * @method  hasParent
     *
     * @param  {Object} model
     * @returns {boolean}     if the model has a parent
     */

    hasParent: function(model) {
      if (!(model instanceof Backbone.Model)) {
        return false;
      }

      return !!FormObjectUtil.parent(model);
    },

    /**
     * @method parent
     *
     * @param  {Object} entity
     * @returns {Object}  the parent of the entity or collection
     */

    parent: function(entity) {
      if (!(entity instanceof Backbone.Model || entity instanceof Backbone.Collection)) {
        Promise.reject(new Error('entity argument must be a Backbone.Model or Backbone.Collection'));
        return;
      }

      if (entity instanceof Backbone.Collection) {
        return entity._parent;
      }
      var collection = entity.collection;
      return collection ? collection._parent : null;
    },

    /**
     * @method hasChildren
     *
     * @param  {Object} model
     * @returns {boolean}    If the model has any children, return true.
     */

    hasChildren: function(model) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      var children = model.get('children');
      return !!(children && children.length > 0);
    },

    /**
     * @method isRootChild
     *
     * @param  {Object} model
     * @returns {boolean} If the model has a parent return false.
     */

    isRootChild: function(model) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      return !FormObjectUtil.hasParent(model);
    },

    /**
     * @method isFirstChild
     *
     * @param  {Object} model
     * @returns {boolean}   If the model is at the first index of the collection
     * return true;
     */

    isFirstChild: function(model) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      var collection = model.collection;
      if (collection) {
        return collection.indexOf(model) == 0;
      }
      return false;
    },

    /**
     * @method isLastChild
     *
     * @param  {Object} model
     * @returns {boolean}  If the index of the model is last, return true.
     */

    isLastChild: function(model) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      var collection = model.collection;
      if (collection) {
        return collection.indexOf(model) == collection.length - 1;
      }
      return false;
    },

    /**
     * @method lastChildIndex
     *
     * @param  {Object} model
     * @returns {number}   The index of the last child
     */

    lastChildIndex: function(model) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      var children = model.get('children');
      if (children) {
        return children.length - 1;
      }
      return -1;
    },

    /**
     * @method childCount
     *
     * @param  {object} model
     * @returns {number}   the number of children in the model
     */

    childCount: function(model) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      var children = model.get('children');
      return children ? children.length : 0;
    },

    /**
     * @method firstChild
     *
     * @param  {Object} model
     * @returns {Object}  the first child of the model
     */

    firstChild: function(model) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      var children = model.get('children');
      if (children instanceof Backbone.Collection) {
        return children.first();
      } else if (_.isArray(children)) {
        return _.first(children);
      }
    },

    /**
     * @method eachChild
     *
     * @param  {List} model   the list to search
     * @param  {Function} fn  iteratee
     * @param  {Object} context
     * @returns {List}
     */

    eachChild: function(model, fn, context) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      var children = model.get('children');
      if (children instanceof Backbone.Collection) {
        children.each(fn, context);
      } else {
        _.each(children, fn, context);
      }
    },

    /**
     * @method indexOfChild
     *
     * @param  {Object} parent
     * @param  {Object} child
     * @returns {number} index or -1
     */

    indexOfChild: function(parent, child) {
      if (!(parent instanceof Backbone.Model)) {
        Promise.reject(new Error('parent argument must be a Backbone.Model'));
        return;
      }

      var children = parent.get('children');

      if (children instanceof Backbone.Collection) {
        return children.indexOf(child);
      } else if (_.isArray(children)) {
        return _.indexOf(children, child);
      }
      return -1;
    },

    /**
     * @method eachDescendent
     *
     * @param  {Object} model
     * @param  {Function} fn   function
     * @param  {Object} context list
     * @param  {Object} options
     * @returns {Object}  this
     */

    eachDescendent: function(model, fn, context, options) {
      if (!(model instanceof Backbone.Model)) {
        Promise.reject(new Error('model argument must be a Backbone.Model'));
        return;
      }

      options || (options = {});

      function iterateChild(child, index, list) {
        var parent = this;
        fn.call(context, child, index, list, parent);
        FormObjectUtil.eachChild(child, iterateChild, child);
      }

      if (options.includeSelf === true) {
        var parentCollection = model.collection;
        fn.call(context, model, parentCollection ? parentCollection.indexOf(model) : null, parentCollection, parent);
      }

      FormObjectUtil.eachChild(model, iterateChild, model);
      return this;
    },

    /**
     * @method insertChild
     *
     * @param  {Object} parent
     * @param  {Object} child  child to add
     * @param  {number} index  index at which to add the clid
     */

    insertChild: function(parent, child, index) {
      if (!(parent instanceof Backbone.Model)) {
        Promise.reject(new Error('parent argument must be a Backbone.Model'));
        return;
      }

      // make sure the child is not inside children already
      // otherwise the insert at will not work
      FormObjectUtil.detachChild(child);
      var children = parent.get('children');
      if (children instanceof Backbone.Collection) {
        children.add(child, { at: index });
      } else if (_.isArray(children)) {
        if (index == null) {
          children.push(child);
        } else {
          children.splice(index, 0, child);
        }
      }
    },

    /**
     * @method detachChild
     *
     * @param  {Object} child
     * @returns {Collection} Collection with detached child
     */

    detachChild: function(child) {
      if (!(child instanceof Backbone.Model)) {
        Promise.reject(new Error('child argument must be a Backbone.Model'));
        return;
      }

      var collection = child.collection;
      if (collection instanceof Backbone.Collection) {
        collection.remove(child);
      }
    },

    /**
     * @method getLeafTemplateObjects
     *
     * @param  {Object} templateObjectModel
     * @returns {Collection}  Collection of templateObject leaves
     */

    getLeafTemplateObjects: function(templateObjectModel) {
      var leafCollection = new Backbone.Collection();

      FormObjectUtil.eachDescendent(templateObjectModel, function(child) {
        if (!FormObjectUtil.hasChildren(child)) {
          leafCollection.add(child);
        }
      });
      return leafCollection;
    },


    /**
     * @method getAnswerValueModel
     * @description This function checks to see if a value Collection has any data objects
     * in it's model collection at a certain index. If it does, it returns said
     * model at index. Otherwise it adds a new id to the collection which, thanks
     * to AnswerValueModel, will set a default value to null.
     * @param {object} model - The model to search.
     * @param {number} index - The index at which to return the data
     * @return {object} Returns an Answer Value model at index/first
     */

    getAnswerValueModel: function(model, index) {
      index = index || 0;
      var valueCollection = FormObjectUtil.getAnswerValueCollection(model);

      while (valueCollection.length <= index) {
        valueCollection.add({
          id: UUID.generate(),
        });
      }

      if (index) {
        return valueCollection.at(index);
      }
      return valueCollection.first();
    },

    /**
     * @method getAnswerValueCollection
     *
     * @param  {Object} templateObject
     * @returns {Collection}  an answerCollection
     */

    getAnswerValueCollection: function(templateObject) {
      var answerModel = FormObjectUtil.getAnswerModel(templateObject);
      var answerCollection = answerModel.get('data');

      if (!(answerCollection instanceof Backbone.Collection)) {
        answerCollection = new Backbone.Collection(answerCollection);
        if (answerModel.id) {
          answerModel.set('data', answerCollection);
        }
      }

      return answerCollection;
    },

    /**
     * @method getAnswerModel
     *
     * @param  {Object} templateObject
     * @returns {Object}  an answer model
     */

    getAnswerModel: function(templateObject) {
      var model = templateObject.get('answer');

      if (!(model instanceof Backbone.Model)) {
        model = new AnswerModel(model);
        if (model.id) {
          templateObject.set('answer', model);
        }
      }
      return model;
    },

    /**
     * @method findChildWithTruthyAnswer
     *
     * @param  {Object} templateObject
     * @param  {number} index
     * @returns {Object}  the templateObject
     */

    findChildWithTruthyAnswer: function(templateObject, index) {
      var templateObject;
      FormObjectUtil.eachChild(templateObject, function(child) {
        var answerValueModel = FormObjectUtil.getAnswerValueModel(child, index);
        if (answerValueModel && answerValueModel.get('value')) {
          templateObject = child;
        }
      });
      return templateObject;
    },

    /**
     * @method triggerBeginEditAnswerEvent
     * @description Called when a user starts editing a templateObject.
     *
     * @param  {Object} answerView
     * @param  {Object} templateObjectModel
     * @param  {Object} form
     * @param  {Object} options
     */

    triggerBeginEditAnswerEvent: function(answerView, templateObjectModel, form, options) {
      var $formObject = answerView.$el.closest('.formObject');
      templateObjectModel.trigger(
        'begin:edit:answer',
        templateObjectModel,
        _.extend(
          {
            answerModel: form.getAnswerModel(templateObjectModel),
            answerView: answerView,
            formObjectView: $formObject.data('formObjectView'),
            model: templateObjectModel,
            templateObjectModel: templateObjectModel,
          },
          options
        )
      );
    },

    /**
     * @method triggerEndEditAnswerEvent
     * @description Called when a user is finished editing a templateObject.
     *
     * @param  {Object} answerView
     * @param  {Object} templateObjectModel
     * @param  {Object} form
     * @param  {Object} options
     */

    triggerEndEditAnswerEvent: function(answerView, templateObjectModel, form, options) {
      var $formObject = answerView.$el.closest('.formObject');
      templateObjectModel.trigger(
        'end:edit:answer',
        templateObjectModel,
        _.extend(
          {
            answerModel: form.getAnswerModel(templateObjectModel),
            answerView: answerView,
            formObjectView: $formObject.data('formObjectView'),
            model: templateObjectModel,
            templateObjectModel: templateObjectModel,
          },
          options
        )
      );
    },
  };

  module.exports = FormObjectUtil;
});

